import { createContext, FC, PropsWithChildren, useCallback, useContext, useMemo, useState } from 'react';
import { getCroppedImg } from '@modules/Tributes/components/CardImageCropper/helpers';

import { FeedbackContext } from '@cvt/contexts';

import { base64ToFile } from '@shared/helpers/file';

import { useMediaCrud } from '@modules/Tributes/hooks/useMediaCrud';
import { useTributeCrud } from '@modules/Tributes/hooks/useTributeCrud';

import { ContextProps, Step } from './TributeCardWizardContext.types';


const STEPS: Step[] = ['selectImage', 'positionImage', 'deliveryDetails', 'review'];

export const defaultContext: ContextProps = {
  tribute: null,
  currentStep: 'selectImage',
  steps: STEPS,
  setCurrentStep: () => {},
  tributeMedia: [],
  onSelectImage: () => {},
  onCreateCard: () => {},
  currentStepIndex: 0,
  selectedImageIndex: undefined,
  selectedCardImage: undefined,
  editedCardImage: undefined,
  deliveryDetails: null,
  onNextStep: () => {},
  onPreviousStep: () => {},
  onSetDeliveryDetails: () => {},
  onSetImageCropData: () => {},
  imageCropData: null,
  croppedImageBase64: '',
};

export const TributeCardWizardContext = createContext(defaultContext);

type Props = {
  tribute: CVT.MaybeNull<Tributes.Tribute>;
};

export const TributeCardWizardProvider: FC<PropsWithChildren<Props>> = (props) => {
  const { children, tribute } = props;

  const { triggerFeedback } = useContext(FeedbackContext);

  const [currentStep, setCurrentStep] = useState<Step>(defaultContext.currentStep);
  const [selectedCardImage, setSelectedCardImage] = useState<Tributes.Media>();
  const [deliveryDetails, setDeliveryDetails] = useState<CVT.MaybeNull<Tributes.DeliveryDetails>>(defaultContext.deliveryDetails);
  const [imageCropData, setImageCropData] = useState<CVT.MaybeNull<Tributes.ImageCropData>>(defaultContext.imageCropData);
  const [croppedImageBase64, setCroppedImageBase64] = useState<CVT.MaybeNull<string>>(defaultContext.croppedImageBase64);

  const { uploadMedia } = useMediaCrud();
  const { createParcel } = useTributeCrud();

  const tributeMedia = useMemo(() => {
    const media = [
      tribute?.mainPhoto,
      ...((tribute?.memories ?? []).map(memory => memory.media).flat()),
    ];
    const images = media.filter(item => item && item.mediaType === 'image');
    return images as Tributes.Media[];
  }, [tribute]);

  const onSetDeliveryDetails = useCallback((details: Tributes.DeliveryDetails) => {
    setDeliveryDetails(details);
  }, []);

  const onSetImageCropData = useCallback(async (data: Tributes.ImageCropData) => {
    setImageCropData(data);
    if (selectedCardImage && data?.pixels) {
      const croppedImage = await getCroppedImg(selectedCardImage.file, data.pixels);
      setCroppedImageBase64(croppedImage);
    }
  }, [selectedCardImage]);

  const onCreateCard = useCallback(async () => {
    const cardData: Tributes.ParcelCrud = {
      destinationAddress: deliveryDetails!,
    };
    if (!croppedImageBase64) {
      console.error('Failed creating parcel: Cropped image base64 is not set');
      return;
    }
    try {
      const fileName = `card-image-${tribute?.id}-${Date.now()}.png`;
      const file = base64ToFile(croppedImageBase64, fileName);
      const media = await uploadMedia.mutateAsync({ media: file });

      cardData.croppedImage = media.data[0].id as unknown as number ;

      await createParcel.mutateAsync({
        tributeId: tribute?.id! as unknown as number,
        payload: cardData,
      });

      triggerFeedback({
        severity: 'success',
        message: 'Tribute card ordered successfully',
      });
    } catch (error) {
      console.error('Failed creating parcel:', error);
    }
  }, [createParcel, croppedImageBase64, deliveryDetails, tribute?.id, triggerFeedback, uploadMedia]);

  const onSelectImage = useCallback((image: Tributes.Media) => {
    setSelectedCardImage((prev)=> {
      if (prev?.id === image.id) {
        return prev;
      }
      setImageCropData(null);
      return image;
    });
  }, []);

  const onNextStep = useCallback(() => {
    const currentIndex = STEPS.indexOf(currentStep);
    if (currentIndex < STEPS.length - 1) {
      setCurrentStep(STEPS[currentIndex + 1]);
    }
  }, [currentStep]);

  const onPreviousStep = useCallback(() => {
    const currentIndex = STEPS.indexOf(currentStep);
    if (currentIndex > 0) {
      setCurrentStep(STEPS[currentIndex - 1]);
    }
  }, [currentStep]);

  return (
    <TributeCardWizardContext.Provider
      value={{
        tribute,
        currentStep,
        steps: STEPS,
        setCurrentStep,
        tributeMedia,
        onSelectImage,
        onCreateCard,
        currentStepIndex: STEPS.indexOf(currentStep),
        selectedImageIndex: tributeMedia.findIndex(media => media.id === selectedCardImage?.id),
        selectedCardImage,
        onNextStep,
        onPreviousStep,
        onSetDeliveryDetails,
        deliveryDetails,
        onSetImageCropData,
        imageCropData,
        croppedImageBase64,
      }}
    >
      {children}
    </TributeCardWizardContext.Provider>
  );
};

export const TributeCardWizardConsumer = TributeCardWizardContext.Consumer;
